import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Mail, Lock, Eye, EyeOff, User, Users, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

// Logo URLs - CFI Branding
const CFI_LOGO = "/images/cfi-logo-main.png";

const LoginPage = () => {
  const { login, isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isClientLogin = searchParams.get('type') === 'client';
  
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginType, setLoginType] = useState(isClientLogin ? 'client' : 'partner');
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      if (user.role === "CREDITFIRMINC_ADMIN" || user.role === "BETENBOUGH_ADMIN" || user.role === "BH_ADMIN" || user.role === "BH_LOCATION_ADMIN") {
        navigate("/admin/dashboard");
      } else if (user.role === "CLIENT") {
        navigate("/client/dashboard");
      } else {
        // All other roles (REFERRAL_PARTNER, BETENBOUGH_TEAM_MEMBER, AFFILIATE, PREFERRED_LENDER, CREDIT_COUNSELOR)
        navigate("/team/dashboard");
      }
    }
  }, [loading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isClientLogin) {
      setLoginType('client');
    }
  }, [isClientLogin]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = await login(formData.email, formData.password);
      
      // Store in localStorage before redirecting
      localStorage.setItem('cfi_user', JSON.stringify(userData));

      // Route based on role - use window.location for full page reload
      let targetPath = "/client/dashboard";
      if (userData.role === "CREDITFIRMINC_ADMIN" || userData.role === "BETENBOUGH_ADMIN" || userData.role === "BH_ADMIN" || userData.role === "BH_LOCATION_ADMIN") {
        targetPath = "/admin/dashboard";
      } else if (userData.role === "BETENBOUGH_TEAM_MEMBER" || userData.role === "REFERRAL_PARTNER") {
        targetPath = "/team/dashboard";
      } else if (userData.role === "CLIENT") {
        targetPath = "/client/dashboard";
      }
      
      // Use window.location for redirect to ensure clean state
      window.location.href = targetPath;
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "Login failed. Please check your credentials.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8f3] flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1a3d18] to-[#265324]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/cfi">
              <img src={CFI_LOGO} alt="Credit Firm Inc" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-2">
              <Button
                variant={loginType === 'partner' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLoginType('partner')}
                className={loginType === 'partner' 
                  ? 'bg-[#84c77f] text-[#1a3d18] hover:bg-[#9ed899]' 
                  : 'border-white/30 text-white hover:bg-white/10'}
              >
                Partner
              </Button>
              <Button
                variant={loginType === 'client' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLoginType('client')}
                className={loginType === 'client' 
                  ? 'bg-[#84c77f] text-[#1a3d18] hover:bg-[#9ed899]' 
                  : 'border-white/30 text-white hover:bg-white/10'}
              >
                Client
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border-[#357c2a]/10" data-testid="login-card">
            <CardHeader className="text-center pb-2">
              <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${
                loginType === 'client' ? 'bg-[#779a86]' : 'bg-[#1a3d18]'
              }`}>
                {loginType === 'client' ? (
                  <User className="h-8 w-8 text-white" />
                ) : (
                  <LogIn className="h-8 w-8 text-[#84c77f]" />
                )}
              </div>
              <CardTitle className="text-2xl text-[#1a3d18]">
                {loginType === 'client' ? 'Client Portal' : 'Partner Portal'}
              </CardTitle>
              <CardDescription>
                {loginType === 'client' 
                  ? 'Track your credit improvement journey' 
                  : 'Sign in to manage your referrals'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="password" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#357c2a]/5">
                  <TabsTrigger 
                    value="password" 
                    className="data-[state=active]:bg-[#357c2a] data-[state=active]:text-white"
                  >
                    Email & Password
                  </TabsTrigger>
                  <TabsTrigger 
                    value="google"
                    className="data-[state=active]:bg-[#357c2a] data-[state=active]:text-white"
                  >
                    Google
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="password">
                  <form onSubmit={handlePasswordLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-[#357c2a]">Email</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#357c2a]/40" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          className="pl-10 border-[#357c2a]/20 focus:border-[#84c77f] focus:ring-[#84c77f]"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="password" className="text-[#357c2a]">Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#357c2a]/40" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className="pl-10 pr-10 border-[#357c2a]/20 focus:border-[#84c77f] focus:ring-[#84c77f]"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#357c2a]/40 hover:text-[#357c2a]"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-[#357c2a] hover:text-[#84c77f] hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#84c77f] text-[#357c2a] hover:bg-[#d4db7e] font-semibold"
                      disabled={isSubmitting}
                      data-testid="login-submit-btn"
                    >
                      {isSubmitting ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="google">
                  <div className="space-y-4">
                    <Button
                      onClick={login}
                      className="w-full bg-[#357c2a] hover:bg-[#2a5f5a]"
                      size="lg"
                      data-testid="google-login-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Sign in with Google
                    </Button>
                    <p className="text-xs text-center text-[#357c2a]/50">
                      Use your Google account for quick access
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Role Info */}
              <div className="mt-6 pt-6 border-t border-[#357c2a]/10">
                {loginType === 'client' ? (
                  <div className="space-y-3 text-sm text-[#357c2a]/70">
                    <div className="flex items-start gap-3 p-3 bg-[#779a86]/10 rounded-lg">
                      <User className="h-5 w-5 text-[#779a86] mt-0.5" />
                      <div>
                        <p className="font-medium text-[#1a3d18]">Welcome Back!</p>
                        <p>Log in to view your credit improvement progress, upcoming tasks, and counselor notes.</p>
                      </div>
                    </div>
                    <p className="text-center text-xs text-[#357c2a]/50">
                      New client? Your credentials were sent to your email when you were registered.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 text-sm text-[#357c2a]/70">
                    <div className="flex items-start gap-3 p-3 bg-[#357c2a]/5 rounded-lg">
                      <div className="w-2 h-2 bg-[#84c77f] rounded-full mt-1.5"></div>
                      <div>
                        <p className="font-medium text-[#1a3d18]">Referral Partner</p>
                        <p>View and manage your referred clients</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-[#357c2a]/5 rounded-lg">
                      <div className="w-2 h-2 bg-[#779a86] rounded-full mt-1.5"></div>
                      <div>
                        <p className="font-medium text-[#1a3d18]">Admin / Counselor</p>
                        <p>Full access to all clients and user management</p>
                      </div>
                    </div>
                    {/* Partner Registration Link */}
                    <div className="pt-3 border-t border-[#357c2a]/10 text-center">
                      <p className="text-sm text-slate-600 mb-2">Don't have an account yet?</p>
                      <Link 
                        to="/register" 
                        className="inline-flex items-center gap-2 text-[#357c2a] hover:text-[#84c77f] font-medium"
                      >
                        <Users className="h-4 w-4" />
                        Create Free Account
                      </Link>
                    </div>
                    {/* Email Change Link */}
                    <div className="text-center">
                      <Link 
                        to="/update-email" 
                        className="text-xs text-slate-500 hover:text-[#357c2a]"
                      >
                        Has your email changed?
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#357c2a]/50 mt-6">
            {loginType === 'client' ? (
              <>
                Are you a partner?{" "}
                <button 
                  onClick={() => setLoginType('partner')} 
                  className="text-[#357c2a] hover:text-[#84c77f] font-medium"
                >
                  Switch to Partner Login
                </button>
              </>
            ) : (
              <>
                Need credit assistance?{" "}
                <Link to="/cfi" className="text-[#357c2a] hover:text-[#84c77f] font-medium">
                  Start your journey
                </Link>
              </>
            )}
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
